import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { QCProcessForm } from "./QCProcessForm";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import type { QCProcessFormValues } from "@/lib/schemas/qc";

export default function QCProcessEdit() {
  const { checkId } = useParams({ strict: false }) as { checkId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: item, isLoading } = useQuery({
    queryKey: ["qcProcess", checkId],
    queryFn: async () =>
      (trpcClient as any).qcProcess.getById.query({ id: checkId }),
    enabled: !!checkId,
  });

  const handleSubmit = async (data: QCProcessFormValues) => {
    try {
      await (trpcClient as any).qcProcess.update.mutate({ id: checkId, data });
      toast.success("Cập nhật biên bản kiểm tra công đoạn (IPQC) thành công!");
      await queryClient.invalidateQueries({ queryKey: ["qcProcess", checkId] });
      await queryClient.invalidateQueries({ queryKey: ["qcProcess"] });
      await queryClient.invalidateQueries({ queryKey: ["qc-process-all"] });
      await queryClient.invalidateQueries({ queryKey: ["po-ipqc-list"] });

      // Rule 3.3 Anti-Form Trap: replace = true
      navigate({
        to: "/qc-process/$checkId",
        params: { checkId },
        replace: true,
      });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cập nhật biên bản IPQC");
      throw err;
    }
  };

  if (isLoading) {
    return (
      <StandardDetailLayout
        breadcrumbs={[
          { label: "QC Công đoạn", to: "/qc-process" },
          { label: "Đang tải..." },
        ]}
        title="Đang tải dữ liệu..."
        onBack={() => navigate({ to: "/qc-process" })}
        showContextPanel={false}
      >
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </StandardDetailLayout>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "QC Công đoạn", to: "/qc-process" },
        {
          label: item?.processId || "Chi tiết",
          to: `/qc-process/${checkId}`,
        },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${item?.processId || checkId}`}
      onBack={() =>
        navigate({ to: "/qc-process/$checkId", params: { checkId } })
      }
      showContextPanel={false}
    >
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-xs">
        <QCProcessForm
          initialData={item}
          onSubmit={handleSubmit}
          onCancel={() =>
            navigate({ to: "/qc-process/$checkId", params: { checkId } })
          }
          isDrawerMode={false}
        />
      </div>
    </StandardDetailLayout>
  );
}
